/**
 * @group Utils

@summary
<blockquote>Builds request configuration for fetch API.</blockquote>

@example
```typescript
const config = buildRequestConfig(apiRequest, defaultHeaders, defaultCredentials);
// Returns: { headers: {...}, body: '...', credentials: 'same-origin' }
```
 * @internal
 */
import type { ApiRequest } from '../types';
import { HTTP_METHODS } from '../constants';

export function buildRequestConfig(
  config: ApiRequest,
  defaultHeaders: Record<string, string>,
  defaultCredentials: RequestCredentials
): {
  headers: Record<string, string>;
  body?: string | FormData;
  credentials: RequestCredentials;
} {
  let headers = { ...defaultHeaders, ...config.headers };
  let body: string | FormData | undefined;

  if (config.body && config.method !== HTTP_METHODS.GET) {
    // Handle FormData specially - don't stringify and don't set Content-Type
    if (config.body instanceof FormData) {
      body = config.body;
      // Remove Content-Type header for FormData - browser will set it with boundary
      const { 'Content-Type': _, ...headersWithoutContentType } = headers;
      headers = headersWithoutContentType;
    } else if (typeof config.body === 'string') {
      body = config.body;
    } else {
      body = JSON.stringify(config.body);
    }
  }

  return {
    headers,
    body,
    credentials: config.credentials || defaultCredentials,
  };
}
